import React from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { paise, num } from "@admin/components/analytics/AnalyticsPrimitives";
import { SettlementActions } from "@admin/components/payouts/SettlementActions";
import { STATUS_LABEL, fmtIST as fmt } from "@admin/components/payouts/settlementFormat";

const MODE = { chat: "Chat", audio: "Audio", video: "Video" };
const EARNING_STATE = {
  reversed: { label: "Refunded / reversed", cls: "bg-rose-100 text-rose-800" },
  paid_out: { label: "Paid out", cls: "bg-[#0A2540] text-white" },
  processing: { label: "Processing", cls: "bg-sky-100 text-sky-800" },
  available: { label: "Released", cls: "bg-emerald-100 text-emerald-800" },
  pending: { label: "Released", cls: "bg-emerald-100 text-emerald-800" },
};

function Row({ label, value, testid }) {
  return (
    <div className="flex justify-between gap-4 py-1.5 border-b border-slate-100 last:border-0 text-sm">
      <span className="text-xs uppercase tracking-widest font-bold text-slate-400">{label}</span>
      <span className="font-bold text-[#0A2540] text-right" data-testid={testid}>{value}</span>
    </div>
  );
}

export function SettlementConfirmDialog({ preview, busy, onCancel, onConfirm }) {
  return (
    <Dialog open={!!preview} onOpenChange={(o) => !o && onCancel()}>
      <DialogContent className="max-w-md" data-testid="settlement-confirm-dialog">
        {preview && (
          <>
            <DialogHeader>
              <DialogTitle style={{ fontFamily: "Poppins" }}>Create Settlement</DialogTitle>
              <DialogDescription>This locks the eligible earnings into a settlement. It is not a payment — the settlement stays unpaid until the bank transfer is completed.</DialogDescription>
            </DialogHeader>
            <Row label="Lawyer" value={preview.lawyer_name} testid="confirm-lawyer" />
            <Row label="Eligible earnings" value={paise(preview.eligible_paise)} testid="confirm-amount" />
            <Row label="Consultations" value={num(preview.items.length)} testid="confirm-count" />
            <Row label="Settlement period" value={preview.period.label} testid="confirm-period" />
            <DialogFooter className="mt-3">
              <Button variant="outline" onClick={onCancel} disabled={busy} data-testid="confirm-cancel">Cancel</Button>
              <Button onClick={onConfirm} disabled={busy || preview.items.length === 0} className="bg-[#0A2540] hover:bg-[#123560]" data-testid="confirm-create">{busy ? "Creating…" : "Create Settlement"}</Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

function ReconciliationBanner({ r }) {
  if (!r) return null;
  const state = !r.applicable ? "na" : r.matches ? "ok" : "mismatch";
  const cls = { ok: "bg-emerald-50 border-emerald-200 text-emerald-900", mismatch: "bg-amber-50 border-amber-300 text-amber-900", na: "bg-slate-50 border-slate-200 text-slate-600" }[state];
  const title = { ok: "Reconciliation OK", mismatch: "Reconciliation mismatch", na: "Reconciliation not applicable" }[state];
  return (
    <div className={`mt-3 rounded-xl border p-3 text-xs ${cls}`} data-testid="detail-reconciliation" data-state={state}>
      <div className="flex items-center justify-between gap-3">
        <strong className="text-sm" data-testid="detail-reconciliation-title">{title}</strong>
        <span className="font-mono" data-testid="detail-reconciliation-summary">
          settlement {paise(r.settlement_amount_paise)} · attributed earnings {paise(r.earnings_sum_paise)} · current ledger {paise(r.ledger_net_paise)}
        </span>
      </div>
      {state === "mismatch" && (
        <ul className="mt-2 list-disc pl-4 space-y-0.5" data-testid="detail-reconciliation-issues">
          {r.issues.map((i) => <li key={i}>{i}</li>)}
          <li className="list-none pl-0 mt-1 text-[11px] opacity-80">Informational only — the historical settlement record is never changed by this view.</li>
        </ul>
      )}
      {state === "na" && <p className="mt-1">{r.issues[0]}</p>}
    </div>
  );
}

export function SettlementDetailDialog({ settlement, onClose, onChanged, readOnly = false }) {
  const s = settlement;
  return (
    <Dialog open={!!s} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl" data-testid="settlement-detail-dialog" data-mode={readOnly ? "history" : "queue"}>
        {s && (
          <>
            <DialogHeader>
              <DialogTitle style={{ fontFamily: "Poppins" }}>Settlement {s.payout_reference || s.payout_id}</DialogTitle>
              <DialogDescription>{readOnly ? "Read-only history record — what was settled or attempted at that point in time." : "Admin financial audit view — lawyer-side earnings only."}</DialogDescription>
            </DialogHeader>
            <div className="grid sm:grid-cols-2 gap-x-8">
              <Row label="Settlement ID" value={s.payout_id} testid="detail-id" />
              <Row label="Lawyer" value={<>{s.lawyer_name}{s.lawyer_resolved === false && <span className="ml-1 text-[10px] text-slate-400 font-normal">(profile unavailable)</span>}</>} testid="detail-lawyer" />
              <Row label="Amount" value={paise(s.amount_paise)} testid="detail-amount" />
              <Row label="Earnings" value={num(s.earning_consultation_ids?.length || 0)} testid="detail-count" />
              <Row label="Period" value={s.settlement_period?.label || "—"} testid="detail-period" />
              <Row label="Status" value={STATUS_LABEL[s.status] || s.status} testid="detail-status" />
              <Row label="Created" value={fmt(s.created_at)} testid="detail-created" />
              <Row label="Created by" value={`${s.initiated_by}${s.created_by?.email ? ` · ${s.created_by.email}` : ""}`} testid="detail-created-by" />
              {s.status === "paid" && <Row label="UTR" value={s.provider_utr || "—"} testid="detail-utr" />}
              {s.status === "paid" && <Row label="Transferred" value={fmt(s.paid_at)} testid="detail-paid-at" />}
              {s.status === "paid" && <Row label="Marked paid by" value={s.paid_by?.email || s.paid_by?.id || "—"} testid="detail-paid-by" />}
              {s.status === "failed" && <Row label="Failure reason" value={s.failure_reason || "—"} testid="detail-failure-reason" />}
              {s.status === "failed" && <Row label="Failed at" value={fmt(s.failed_at)} testid="detail-failed-at" />}
            </div>
            {!readOnly && <SettlementActions settlement={s} onDone={(p) => onChanged && onChanged(p)} />}
            <ReconciliationBanner r={s.reconciliation} />
            <div className="mt-3 border border-slate-200 rounded-xl overflow-hidden max-h-72 overflow-y-auto">
              <table className="w-full text-sm" data-testid="detail-earnings-table">
                <thead className="bg-slate-50 text-slate-500"><tr>{["Date", "Consultation", "Mode", "Duration", "Lawyer earnings", "Current state"].map((h) => <th key={h} className="text-left font-semibold px-3 py-2 text-[11px] uppercase tracking-wider">{h}</th>)}</tr></thead>
                <tbody>
                  {(s.earnings || []).map((e) => {
                    const elsewhere = e.payout && e.payout.payout_id !== s.payout_id;
                    const st = EARNING_STATE[e.state] || { label: e.state, cls: "bg-slate-100 text-slate-700" };
                    return (
                      <tr key={e.consultation_id} className="border-t border-slate-100" data-testid={`detail-earning-${e.consultation_id}`}>
                        <td className="px-3 py-2 text-xs text-slate-500">{fmt(e.date)}</td>
                        <td className="px-3 py-2 font-bold text-[#0A2540]">#{e.consultation_id.slice(-8).toUpperCase()}</td>
                        <td className="px-3 py-2 text-xs">{MODE[e.mode] || e.mode}</td>
                        <td className="px-3 py-2 text-xs">{e.duration_minutes} min</td>
                        <td className="px-3 py-2 font-extrabold text-[#0A2540]">{paise(e.amount_paise)}</td>
                        <td className="px-3 py-2 text-xs">
                          <span className={`text-[10px] uppercase font-bold tracking-wider px-2 py-0.5 rounded-full ${st.cls}`} data-testid={`detail-earning-state-${e.consultation_id}`}>{st.label}</span>
                          {elsewhere && <span className="ml-1 text-[10px] text-slate-500" data-testid={`detail-earning-resettled-${e.consultation_id}`}>now in {e.payout.payout_reference || e.payout.payout_id}</span>}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
